import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Activity,
  Users,
  Eye,
  MousePointer,
  Clock,
  Smartphone,
  Monitor,
  Tablet,
  Globe,
  TrendingUp,
  MessageCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Map,
  ArrowDown,
  Target,
  Zap
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLiveActivity = () => {
  const [loading, setLoading] = useState(true);
  const [liveData, setLiveData] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const feedEndRef = useRef(null);

  useEffect(() => {
    fetchLiveData();
  }, []);

  // Auto-refresh every 3 seconds when enabled
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchLiveData(true); // silent refresh
    }, 3000);
    
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchLiveData = async (silent = false) => {
    try {
      const token = localStorage.getItem("admin_token");
      const headers = { Authorization: `Bearer ${token}` };

      const [liveRes, eventsRes] = await Promise.all([
        axios.get(`${API}/admin/live-tracking`, { headers }),
        axios.get(`${API}/admin/analytics-v2/events?limit=50`, { headers })
      ]);

      setLiveData(liveRes.data);
      
      // Process events into activity feed format
      const events = eventsRes.data.events || [];
      const feedItems = events.map(event => ({
        id: event.id,
        session_id: event.session_id,
        type: event.event_name,
        payload: event.event_payload,
        device: event.device_info,
        page: event.page_path,
        timestamp: event.created_at
      }));
      setActivityFeed(feedItems);

    } catch (error) {
      if (!silent) {
        console.error("Failed to fetch live data:", error);
        toast.error("Failed to load live data");
      }
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (type) => {
    const icons = {
      'page_view': <Eye className="w-4 h-4 text-blue-500" />,
      'heartbeat': <Activity className="w-4 h-4 text-green-500" />,
      'scroll_depth': <ArrowDown className="w-4 h-4 text-purple-500" />,
      'click': <MousePointer className="w-4 h-4 text-orange-500" />,
      'cta_click': <Target className="w-4 h-4 text-red-500" />,
      'form_start': <Zap className="w-4 h-4 text-yellow-500" />,
      'form_submit': <Zap className="w-4 h-4 text-green-600" />,
      'chat_widget_opened': <MessageCircle className="w-4 h-4 text-[#B08968]" />,
      'duration_selected': <Clock className="w-4 h-4 text-indigo-500" />,
      'payment_mode_selected': <TrendingUp className="w-4 h-4 text-emerald-500" />,
      'exit_intent': <Globe className="w-4 h-4 text-gray-500" />,
    };
    return icons[type] || <Activity className="w-4 h-4 text-gray-400" />;
  };

  const getEventDescription = (event) => {
    const { type, payload, page } = event;
    
    switch (type) {
      case 'page_view':
        return `Viewed ${page || 'page'}`;
      case 'heartbeat':
        return `Active on page (${payload?.scroll_position || 0}% scrolled, ${payload?.time_on_page || 0}s)`;
      case 'scroll_depth':
        return `Scrolled to ${payload?.depth_percent}%`;
      case 'click':
        return `Clicked ${payload?.element_text || payload?.element_tag || 'element'}`;
      case 'cta_click':
        return `Clicked CTA: ${payload?.text || 'button'}`;
      case 'form_start':
        return `Started form: ${payload?.form_name || 'form'}`;
      case 'form_submit':
        return `Submitted form: ${payload?.form_name || 'form'}`;
      case 'chat_widget_opened':
        return 'Opened chat widget';
      case 'duration_selected':
        return `Selected ${payload?.hours}hr duration`;
      case 'payment_mode_selected':
        return `Selected ${payload?.advance_percent}% payment`;
      case 'exit_intent':
        return 'Showed exit intent';
      case 'lead_modal_opened':
        return 'Opened lead capture form';
      case 'lead_captured':
        return `Submitted lead (₹${(payload?.grand_total || 0).toLocaleString()})`;
      case 'lock_deal_started':
        return 'Started locking deal';
      case 'lock_deal_success':
        return `Locked deal (₹${(payload?.grand_total || 0).toLocaleString()})`;
      case 'pdf_download_started':
        return 'Started PDF download';
      case 'pdf_downloaded':
        return 'Downloaded PDF estimate';
      default:
        return type.replace(/_/g, ' ');
    }
  };

  const getDeviceIcon = (deviceType) => {
    if (deviceType === 'mobile') return <Smartphone className="w-4 h-4" />;
    if (deviceType === 'tablet') return <Tablet className="w-4 h-4" />;
    return <Monitor className="w-4 h-4" />;
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    const date = new Date(isoStr);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    
    if (diffSec < 5) return 'Just now';
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const getSessionColor = (sessionId) => {
    // Generate consistent color based on session ID
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500',
      'bg-pink-500', 'bg-indigo-500', 'bg-teal-500', 'bg-red-500'
    ];
    const hash = sessionId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-[#B08968]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Activity className="w-6 h-6 text-[#B08968]" />
              Live Activity Monitor
            </h1>
            <p className="text-gray-600 mt-1">
              Real-time view of user behavior on your website
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300"
              />
              Auto-refresh
            </label>
            <Button
              variant="outline"
              onClick={() => fetchLiveData()}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Live Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-green-700">
                  {liveData?.stats?.active_visitors || 0}
                </p>
                <p className="text-sm text-green-600">Active Now</p>
              </div>
              {liveData?.stats?.active_visitors > 0 && (
                <span className="ml-auto w-3 h-3 bg-green-500 rounded-full animate-pulse" />
              )}
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-blue-700">
                  {liveData?.stats?.currently_chatting || 0}
                </p>
                <p className="text-sm text-blue-600">Chatting</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500 rounded-lg">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-amber-700">
                  {liveData?.stats?.high_intent_visitors || 0}
                </p>
                <p className="text-sm text-amber-600">High Intent</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border border-red-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500 rounded-lg">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-red-700">
                  {liveData?.stats?.waiting_for_handover || 0}
                </p>
                <p className="text-sm text-red-600">Need Attention</p>
              </div>
              {liveData?.stats?.waiting_for_handover > 0 && (
                <span className="ml-auto w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              )}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Active Sessions Panel */}
          <div className="lg:col-span-1 bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#B08968]" />
                Active Visitors ({liveData?.active_sessions?.length || 0})
              </h3>
            </div>
            <div className="divide-y max-h-[500px] overflow-y-auto">
              {liveData?.active_sessions?.length > 0 ? (
                liveData.active_sessions.map((session, idx) => {
                  const lastEvent = activityFeed.find(e => e.session_id === session.session_id);
                  const heartbeat = activityFeed.find(
                    e => e.session_id === session.session_id && e.type === 'heartbeat'
                  );
                  
                  return (
                    <div
                      key={session.session_id || idx}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedSession === session.session_id ? 'bg-[#B08968]/5 border-l-4 border-l-[#B08968]' : ''
                      }`}
                      onClick={() => setSelectedSession(
                        selectedSession === session.session_id ? null : session.session_id
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {/* Session Avatar */}
                        <div className={`w-10 h-10 rounded-full ${getSessionColor(session.session_id)} flex items-center justify-center text-white`}>
                          {getDeviceIcon(session.device_info?.device_type)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-gray-900">
                              {session.device_info?.device_type || 'Unknown'} visitor
                            </span>
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                          </div>
                          <p className="text-xs text-gray-500 truncate">
                            {session.current_page || '/weddings'}
                          </p>
                        </div>

                        {/* Live Scroll Indicator */}
                        {heartbeat && (
                          <div className="text-right">
                            <div className="text-xs font-medium text-gray-700">
                              {heartbeat.payload?.scroll_position || 0}%
                            </div>
                            <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-[#B08968] rounded-full transition-all duration-300"
                                style={{ width: `${heartbeat.payload?.scroll_position || 0}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Expanded Details */}
                      {selectedSession === session.session_id && (
                        <div className="mt-3 pt-3 border-t space-y-2">
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="bg-gray-100 rounded p-2">
                              <span className="text-gray-500">Browser</span>
                              <p className="font-medium">{session.device_info?.browser || 'Unknown'}</p>
                            </div>
                            <div className="bg-gray-100 rounded p-2">
                              <span className="text-gray-500">OS</span>
                              <p className="font-medium">{session.device_info?.os || 'Unknown'}</p>
                            </div>
                            {heartbeat && (
                              <>
                                <div className="bg-gray-100 rounded p-2">
                                  <span className="text-gray-500">Time on Page</span>
                                  <p className="font-medium">{heartbeat.payload?.time_on_page || 0}s</p>
                                </div>
                                <div className="bg-gray-100 rounded p-2">
                                  <span className="text-gray-500">Scroll</span>
                                  <p className="font-medium">{heartbeat.payload?.scroll_position || 0}%</p>
                                </div>
                              </>
                            )}
                          </div>
                          
                          {/* Intent Badge */}
                          {session.intent && (
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                session.intent.primary_intent === 'ready_to_book'
                                  ? 'bg-green-100 text-green-700'
                                  : session.intent.primary_intent === 'comparing'
                                  ? 'bg-purple-100 text-purple-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                Intent: {session.intent.primary_intent}
                              </span>
                              {session.intent.high_intent_ready > 50 && (
                                <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                                  High Intent
                                </span>
                              )}
                            </div>
                          )}

                          {/* Chat Status */}
                          {session.chat_status && (
                            <div className="flex items-center gap-2">
                              <MessageCircle className="w-3 h-3 text-blue-500" />
                              <span className="text-xs text-blue-600">
                                Chat: {session.chat_status}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No active visitors</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Visitors will appear here in real-time
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Live Activity Feed */}
          <div className="lg:col-span-2 bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#B08968]" />
                Live Activity Feed
              </h3>
              <span className="text-xs text-gray-500">
                {activityFeed.length} events
              </span>
            </div>
            <div className="divide-y max-h-[500px] overflow-y-auto">
              {activityFeed.length > 0 ? (
                activityFeed
                  .filter(event => !selectedSession || event.session_id === selectedSession)
                  .map((event, idx) => (
                    <div
                      key={event.id || idx}
                      className={`p-3 hover:bg-gray-50 transition-colors ${
                        event.type === 'heartbeat' ? 'bg-green-50/50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Event Icon */}
                        <div className="mt-0.5">
                          {getEventIcon(event.type)}
                        </div>

                        {/* Event Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-gray-900">
                              {getEventDescription(event)}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              {getDeviceIcon(event.device?.device_type)}
                              {event.device?.device_type || 'Unknown'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              {event.page || '/weddings'}
                            </span>
                          </div>
                        </div>

                        {/* Timestamp */}
                        <div className="text-xs text-gray-400 whitespace-nowrap">
                          {formatTime(event.timestamp)}
                        </div>

                        {/* Session Indicator */}
                        <div 
                          className={`w-2 h-2 rounded-full ${getSessionColor(event.session_id)}`}
                          title={`Session: ${event.session_id.slice(0, 8)}...`}
                        />
                      </div>
                    </div>
                  ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No activity yet</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Events will appear here as users interact
                  </p>
                </div>
              )}
              <div ref={feedEndRef} />
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Event Types</h4>
          <div className="flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5 text-blue-500" />
              <span>Page View</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-green-500" />
              <span>Heartbeat (Active)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ArrowDown className="w-3.5 h-3.5 text-purple-500" />
              <span>Scroll Depth</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MousePointer className="w-3.5 h-3.5 text-orange-500" />
              <span>Click</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Target className="w-3.5 h-3.5 text-red-500" />
              <span>CTA Click</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MessageCircle className="w-3.5 h-3.5 text-[#B08968]" />
              <span>Chat Opened</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-yellow-500" />
              <span>Form Interaction</span>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminLiveActivity;
